import { useState } from "react";
import { ago } from "../../lib/dash";
import { Tile, Row, Chip } from "./Tile";

const balanceText = (r, live) => {
  if (!r.connected) return "not connected";
  if (r.error) return r.error;
  if (r.balance == null) return live ? "not checked yet" : "key set · checked in production only";
  return `${r.balance} ${r.unit}`;
};

const BalanceRow = ({ r, live, owner, onSave }) => {
  const [low, setLow] = useState(String(r.low));
  const [busy, setBusy] = useState(false);
  const isLow = r.balance != null && r.balance < r.low;
  const save = async () => {
    setBusy(true);
    await onSave({ provider: r.id, threshold: Number(low) });
    setBusy(false);
  };
  return (
    <li data-testid={`balance-${r.id}`} className="flex items-center gap-2 border-b border-border/60 py-1 text-[12.5px] last:border-0">
      <span className="w-24 shrink-0 truncate font-semibold">{r.label}</span>
      <span data-testid={`balance-${r.id}-value`} className={`min-w-0 flex-1 truncate ${isLow ? "font-semibold text-amber-700" : r.error ? "text-rose-600" : "text-muted-foreground"}`}>
        {balanceText(r, live)}
      </span>
      {owner ? (
        <span className="flex shrink-0 items-center gap-1">
          <span className="text-[11px] text-muted-foreground">low under</span>
          <input data-testid={`balance-${r.id}-low`} type="number" min="0" value={low} onChange={(e) => setLow(e.target.value)}
            className="h-6 w-16 rounded-md border border-input bg-background px-1.5 text-right text-xs tabular-nums" />
          <button type="button" data-testid={`balance-${r.id}-save`} disabled={busy || low === "" || Number(low) === r.low} onClick={save}
            className="h-6 rounded-md bg-foreground px-2 text-[11px] font-semibold text-background transition-opacity hover:opacity-85 disabled:opacity-40">Save</button>
        </span>
      ) : (
        <span data-testid={`balance-${r.id}-low`} className="shrink-0 text-[11px] text-muted-foreground">low under {r.low} {r.unit}</span>
      )}
    </li>
  );
};

export const AlertStatusTile = ({ d, owner, onSave, index }) => {
  const { email, balances: b, last_run: run } = d;
  const failed = Object.keys(run?.errors || {}).length;
  return (
    <Tile id="alerts-status" index={index} kicker="Tile 9 · status" title="Alert email and provider balances"
      action={<Chip testid="alerts-email-mode" tone={email.live ? "green" : undefined}>{email.live ? "Emails live" : `${email.environment}: not emailed`}</Chip>}>
      <div className="dash-scroll min-h-0 flex-1 overflow-y-auto pr-1">
        <Row dense label="Alert emails go to" testid="alerts-email-to" tone={email.to ? undefined : "amber"} value={email.to ? `${email.to} (owner only)` : "OWNER_ALERT_EMAIL is not set"} />
        <Row dense label="Last alert email" testid="alerts-email-last" tone={email.last?.status === "failed" ? "red" : undefined}
          value={email.last ? `${email.last.status} · ${ago(email.last.created_at)}` : "none yet"} />
        <Row dense label="Last run" testid="alerts-last-run" tone={failed ? "amber" : undefined}
          value={run ? `${ago(run.at)} · ${run.took_ms} ms${failed ? ` · ${failed} rule${failed > 1 ? "s" : ""} failed` : ""}` : "not run here yet"} />
        <p className="mt-2 font-mono2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          Provider balances · {b.live ? (b.checked_at ? `checked ${ago(b.checked_at)}` : "not checked yet") : "checked in production only"}
        </p>
        <ul data-testid="alerts-balances">{b.rows.map((r) => <BalanceRow key={`${r.id}-${r.low}`} r={r} live={b.live} owner={owner} onSave={onSave} />)}</ul>
        {b.not_available.map((n) => (
          <p key={n.id} data-testid={`balance-${n.id}`} className="pt-1 text-[11.5px] text-muted-foreground"><span className="font-semibold text-foreground">{n.label}</span> · {n.note}</p>
        ))}
      </div>
    </Tile>
  );
};
